export interface ChainConfig {
    bulbRadius: number;
    bulbsCount: number;
    endColor: string;
    glowOffset: number;
    opacity: number;
    startAngle: number;
    startColor: string;
    turnsCount: number;
}

interface Point {
    x: number;
    y: number;
}

let ctx: CanvasRenderingContext2D | null = null;
let rotationZ = 0;

let width = 0;
let height = 0;
let treeHeight = 0;
let baseRadius = 0;
let baseCenter: Point = { x: 0, y: 0 };

let tiltAngle = 0;
const rotationX = 30;

const chains: ChainConfig[] = [
    {
        bulbRadius: 7,
        bulbsCount: 100,
        endColor: '#2da418',
        glowOffset: 0,
        opacity: 1,
        startAngle: 0,
        startColor: '#18a452',
        turnsCount: 14,
    },
    {
        bulbRadius: 50,
        bulbsCount: 20,
        endColor: '#13d116',
        glowOffset: 0,
        opacity: 0.5,
        startAngle: 120,
        startColor: '#18a452',
        turnsCount: 3,
    },
    {
        bulbRadius: 12,
        bulbsCount: 50,
        endColor: '#13d116',
        glowOffset: 0,
        opacity: 0.8,
        startAngle: 240,
        startColor: '#18a452',
        turnsCount: -3,
    },
    {
        bulbRadius: 7,
        bulbsCount: 196,
        endColor: '#e9841d',
        glowOffset: 0,
        opacity: 0.8,
        startAngle: 240,
        startColor: '#e9a01b',
        turnsCount: -3,
    },
];

const hexToRgb = (hex: string): number[] => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [255, 255, 255];
};

const mixColor = (a: string, b: string, t: number): string => {
    const c1 = hexToRgb(a);
    const c2 = hexToRgb(b);
    const r = Math.round(c1[0] * (1 - t) + c2[0] * t);
    const g = Math.round(c1[1] * (1 - t) + c2[1] * t);
    const b2 = Math.round(c1[2] * (1 - t) + c2[2] * t);
    return `rgb(${r}, ${g}, ${b2})`;
};

const applyOpacity = (color: string, alpha: number): string => {
    if (color.startsWith('#')) {
        const [r, g, b] = hexToRgb(color);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
};

const renderChain = (chain: ChainConfig) => {
    if (!ctx) return;

    for (let i = 0; i < chain.bulbsCount; i++) {
        let progress = i / (chain.bulbsCount - 1);
        progress = Math.pow(progress, Math.sqrt(progress) + 1);

        const turnProgress = (progress * chain.turnsCount) % 1;

        const radius = baseRadius * (1 - progress);
        const angle = (((turnProgress * 360 + chain.startAngle + rotationZ) * Math.PI) / 180) % (Math.PI * 2);

        const x = baseCenter.x + Math.sin(angle) * radius;
        const y =
            baseCenter.y -
            progress * treeHeight * Math.sin(((90 - rotationX) * Math.PI) / 180) +
            radius * Math.sin(tiltAngle) * Math.cos(angle);

        const bulbRadiusCalc = (chain.bulbRadius * treeHeight) / 1000;
        const glowRadiusCalc = ((chain.bulbRadius + chain.glowOffset) * treeHeight) / 1000;

        const depthOpacity = Math.min(1, Math.max(0.35, Math.cos(angle)) + 0.3);
        const mixed = mixColor(chain.startColor, chain.endColor, progress);
        const color = applyOpacity(mixed, depthOpacity);

        ctx.globalAlpha = chain.opacity;

        if (chain.glowOffset > 0) {
            const gradient = ctx.createRadialGradient(x, y, bulbRadiusCalc, x, y, glowRadiusCalc);
            gradient.addColorStop(0, applyOpacity(mixColor(mixed, '#fff', 0.1), 0.5));
            gradient.addColorStop(0.5, applyOpacity(mixed, 0.3));
            gradient.addColorStop(1, applyOpacity(mixed, 0));

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(x, y, glowRadiusCalc, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, bulbRadiusCalc, 0, Math.PI * 2);
        ctx.fill();
    }
};

const loop = () => {
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);
    rotationZ = (rotationZ - 1) % 360;

    chains.forEach(renderChain);
    requestAnimationFrame(loop);
};

const resize = (canvas: HTMLCanvasElement) => {
    if (!ctx) return;

    const parent = canvas.parentElement || document.body;
    const cw = parent.clientWidth;
    const ch = parent.clientHeight;

    const pixelRatio = window.devicePixelRatio || 1;

    width = cw;
    height = ch;

    canvas.width = cw * pixelRatio;
    canvas.height = ch * pixelRatio;

    ctx.scale(pixelRatio, pixelRatio);

    tiltAngle = (rotationX * Math.PI) / 180;

    treeHeight = Math.min(cw, ch) * 0.8;
    baseRadius = treeHeight * 0.3;

    baseCenter = {
        x: cw / 2,
        y: ch / 2 + (treeHeight / 2) * Math.cos(tiltAngle) - (baseRadius / 2) * Math.sin(tiltAngle),
    };
};

export const startXmasTreeAnimation = (canvas: HTMLCanvasElement) => {
    ctx = canvas.getContext('2d');
    if (!ctx) return;

    resize(canvas);
    window.addEventListener('resize', () => resize(canvas));

    loop();
};
